import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, ChevronRightIcon } from 'lucide-react';
import { useProto } from '../../contexts/PrototypeContext';
import { volunteerIntro } from '../../data/registration';
import { formatMoney, formatOfferAmount } from '../../utils/format';
import { VolunteerDiscountCard } from './VolunteerDiscountCard';
import { CoachCreditCard } from './CoachCreditCard';
import { VolunteerOptions } from './VolunteerOptions';
import { SomeoneElseForm, someoneErrors } from './SomeoneElseForm';
import { StepNotice } from './StepNotice';
import { Annotation } from '../shared/Annotation';
const ease = [0.23, 1, 0.32, 1] as const;
export function VolunteerInfoStep({
  device
}: {
  device: 'mobile' | 'desktop';
}) {
  const {
    state,
    cart,
    discountApplied,
    actions
  } = useProto();
  const [showErrors, setShowErrors] = useState(false);
  const [choiceError, setChoiceError] = useState(false);
  const desktop = device === 'desktop';
  const eligible = state.choice === 'myself' || state.choice === 'someone';
  useEffect(() => {
    setShowErrors(false);
    setChoiceError(false);
  }, [state.choice, state.activeScenario]);
  function next() {
    if (!state.choice) return setChoiceError(true);
    if (state.choice === 'someone' && Object.keys(someoneErrors(state.someone)).length) return setShowErrors(true);
    actions.goToStep('cart');
  }
  return <div className={desktop ? 'mx-auto max-w-[935px] space-y-5 px-8 py-12' : 'space-y-4 px-4 py-5'} data-id="element-358" data-mp="30icla1onuw99">
      <section className={`rounded border border-i9-skyLine bg-i9-sky text-center font-i9 text-i9-teal ${desktop ? 'px-12 py-8' : 'px-4 py-5'}`} data-id="element-359" data-mp="2wiafphft1iop">
        <h1 className={`font-bold leading-snug ${desktop ? 'text-[21px]' : 'text-[17px]'}`} data-id="element-360" data-mp="35gmei6qtrms8">{volunteerIntro.heading}</h1>
        <p className={`mt-3 leading-relaxed ${desktop ? 'text-[15px]' : 'text-[13px]'}`} data-id="element-361" data-mp="poyn0qukymgt">
          {volunteerIntro.body} <strong className="font-bold" data-id="element-362" data-mp="kqex6fcv8qj6">{volunteerIntro.emphasis}</strong>
        </p>
      </section>

      <StepNotice data-id="element-363" data-mp="sr314kcyu0gc" />
      {(state.notice === 'unavailable' || state.notice === 'expired-unavailable') && <Annotation kind="unresolved" title="Decision #4 · messaging when capacity changes" data-id="element-364" data-mp="wreldw1f1yra">
          Copy and treatment for an offer that was visible but is gone before a reservation is created are not final. Also confirm whether to swap in Automated Coach Credit immediately (shown here when enabled).
        </Annotation>}

      <AnimatePresence mode="wait" initial={false} data-id="element-365" data-mp="e2q55ndvohlu">
        {state.pageOffer !== 'none' && <motion.div key={state.pageOffer} initial={{
        opacity: 0,
        scale: 0.98
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.98
      }} transition={{
        duration: 0.2,
        ease
      }} className={desktop ? 'mx-auto max-w-[885px]' : ''} data-id="element-366" data-mp="j1tbdb5lgtve">
            {state.pageOffer === 'discount' ? <VolunteerDiscountCard amount={state.config.amount} message={state.config.message} size={device} data-id="element-367" data-mp="3rfjqk9tbopsf" /> : <CoachCreditCard size={device} data-id="element-368" data-mp="3ezab45nyvoc9" />}
          </motion.div>}
      </AnimatePresence>
      {state.pageOffer === 'discount' && !state.choice && <Annotation kind="rule" title="Viewing doesn't reserve" data-id="element-369" data-mp="3a20laowo1anw">
          No opportunity is held until the customer selects an eligible volunteer option; availability is revalidated server-side at that moment.
        </Annotation>}

      <div className={desktop ? 'pt-2' : ''} data-id="element-370" data-mp="2w89dpm2hw0ut">
        <VolunteerOptions size={device} data-id="element-371" data-mp="2w77yr6icyu7t" />
        {choiceError && <p role="alert" className="mt-2 text-center font-i9 text-[13px] font-medium text-i9-red" data-id="element-372" data-mp="2r8n37ke1wypp">Choose a volunteer option to continue.</p>}
      </div>

      <AnimatePresence initial={false} data-id="element-373" data-mp="2m8ii1h277ft5">
        {discountApplied && <motion.div key="applied" initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} role="status" className={`flex items-center gap-2 rounded-md bg-i9-green px-3 py-2.5 font-i9 text-[14px] font-semibold text-i9-greenInk ${desktop ? 'mx-auto w-fit px-5' : ''}`} data-id="element-374" data-mp="2haqsuusxu7sn">
            <CheckCircle2Icon className="h-5 w-5 shrink-0 text-i9-greenIcon" aria-hidden="true" data-id="element-375" data-mp="2t66hb9goxt2g" />
            {formatOfferAmount(state.config.amount)} Volunteer Registration Discount applied to your cart
          </motion.div>}
      </AnimatePresence>
      {discountApplied && <>
          <Annotation kind="placeholder" title="Decision #1 · reservation placed (30 min placeholder)" data-id="element-376" data-mp="2o6jxuia9rgl7">
            Selection triggered revalidation and a temporary hold. Hold duration is not finalized.
          </Annotation>
          <Annotation kind="rule" title="No background check required" data-id="element-377" data-mp="8h8szsuq9et6">
            The immediate discount is awarded on the volunteer commitment alone. Background checks apply only to Automated Coach Credit.
          </Annotation>
        </>}

      {state.choice === 'someone' && <div className={desktop ? 'mx-auto max-w-[620px] space-y-3' : 'space-y-3'} data-id="element-378" data-mp="1t02p0nr0n321">
          <SomeoneElseForm showErrors={showErrors} size={device} data-id="element-379" data-mp="26r7ljgz37f85" />
          <Annotation kind="rule" title="Someone Else stays eligible" data-id="element-380" data-mp="26siml915e8vk">
            Uses the existing volunteer capture fields. No separate consent workflow for the pilot.
          </Annotation>
        </div>}
      {state.choice === 'maybe' && <p className={`font-i9 text-[14px] leading-relaxed text-i9-muted ${desktop ? 'text-center' : ''}`} data-id="element-381" data-mp="2bqtxzo73crfw">
          Great! Your local i9 Sports team will reach out with more information about volunteering.
        </p>}
      {eligible && state.pageOffer === 'coachCredit' && <p className={`font-i9 text-[14px] leading-relaxed text-i9-muted ${desktop ? 'text-center' : ''}`} data-id="element-382" data-mp="vbsmm7nbidae">
          Thanks for volunteering! If you coach and complete the requirements, you may qualify for Automated Coach Credit toward a future season.
        </p>}

      <div className={`border-t border-i9-line ${desktop ? 'mx-[-40px] pt-10 text-center' : 'pt-4'}`} data-id="element-383" data-mp="2qhbhba69vo8z">
        <p className={`mb-3 font-i9 text-[14px] text-i9-muted ${desktop ? '' : 'flex justify-between'}`} data-id="element-384" data-mp="2vg4xjacy1tcu">
          <span data-id="element-385" data-mp="34f04iontb7h7">Cart total </span>
          <motion.span key={cart.total} initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.2,
          ease
        }} className="inline-block font-bold tabular-nums text-i9-ink" data-id="element-386" data-mp="34f8h0fg0warv">
            {formatMoney(cart.total)}
          </motion.span>
        </p>
        <button type="button" onClick={next} className={`inline-flex items-center justify-between gap-4 bg-i9-steel font-i9 font-bold text-white shadow-md transition-colors duration-150 hover:bg-i9-steelDark ${desktop ? 'px-10 py-4 text-[18px]' : 'w-full px-5 py-4 text-[17px]'}`} data-id="element-387" data-mp="3j5zadrx9jhx3">
          Next: Cart
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-388" data-mp="1lh3kebut12an" />
        </button>
      </div>
    </div>;
}